import { useState } from "react";
import { clsx } from "clsx";
import Collapsible from "react-collapsible";
import { Spinner } from "react-bootstrap";
import Typography from "../../../../../../../components/typography/Typography";
import { Link } from "../../../../../../../components/link";
import Icon from "../../../../../../../components/Icon";
import { LLMThinkingStep } from "../@types/LLMSummary";
import styles from "../llm-summary.module.scss";
import { useLocalSampleSummaryParams } from "../../../../../hooks";
import { pathToSS } from "../../../../../util";
import { SECTIONS } from "../../../../../consts";

type Props = {
  thinkingSteps: LLMThinkingStep[];
  isProcessing?: boolean;
};

const SECTION_LABELS: Record<string, string> = {
  static: "Static Analysis",
  dynamic: "Dynamic Analysis",
  cloud_sandbox: "Cloud Sandbox",
  auxiliary_analysis: "Auxiliary Analysis",
  av_scanners: "AV Scanners",
  yara: "Yara",
  extracted_files: "Extracted Files",
  relationships: "Relationships",
  networking: "Network",
  community: "Community",
  cloud_static: "Cloud Static Analysis",
  cloud_dynamic: "Cloud Dynamic Analysis",
  cloud_xref: "Cloud XRef",
  cloud_rca2: "Cloud RCA2"
};

const SECTION_LINKS: Record<string, string> = {
  static: SECTIONS.TI_CORE.FILE_INFO,
  dynamic: SECTIONS.FILE_ANALYSIS.ANALYSIS_RESULTS,
  cloud_sandbox: SECTIONS.DYNAMIC_ANALYSIS.CLOUD_SANDBOX,
  auxiliary_analysis: SECTIONS.AUXILIARY_ANALYSIS.INDEX,
  av_scanners: SECTIONS.THREAT_INTELLIGENCE.MULTISCANNER,
  yara: SECTIONS.TI_CORE.YARA,
  extracted_files: SECTIONS.EXTRACTED_FILES.INDEX,
  relationships: SECTIONS.FILE_ANALYSIS.RELATIONSHIPS_GRAPH,
  networking: SECTIONS.FILE_ANALYSIS.NETWORK_REFERENCES,
  community: SECTIONS.FILE_ANALYSIS.SAMPLE_DETAILS,
  cloud_static: SECTIONS.THREAT_INTELLIGENCE.MULTISCANNER,
  cloud_dynamic: SECTIONS.FILE_ANALYSIS.ANALYSIS_RESULTS,
  cloud_xref: SECTIONS.THREAT_INTELLIGENCE.MULTISCANNER,
  cloud_rca2: SECTIONS.THREAT_INTELLIGENCE.MULTISCANNER
};

const ThinkingStepItem = ({ step }: { step: LLMThinkingStep }) => {
  const [isOpen, setIsOpen] = useState(true);
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();

  const sectionLabels = step.sections?.map(s => {
    if (SECTION_LINKS[s]) {
      return (
        <Link to={pathToSS(hash, sourceId, SECTION_LINKS[s], relationshipId)}>
          {SECTION_LABELS[s] || s}
        </Link>
      );
    } else {
      return s;
    }
  });

  return (
    <div className="flex flex-column flex-gap-1">
      <Typography
        variant="body2"
        tag="span"
        className={`mb-10 ${styles.llm_text_gray}`}
      >
        Searched{" "}
        {sectionLabels?.length
          ? sectionLabels?.map((label, i) => (
              <span>
                {label}
                {i === sectionLabels.length - 1 ? "" : ", "}
              </span>
            ))
          : "general info"}
      </Typography>

      <Collapsible
        open={isOpen}
        handleTriggerClick={() => setIsOpen(!isOpen)}
        trigger={
          <div
            className="flex align-center flex-gap-1"
            style={{ cursor: "pointer" }}
          >
            <Typography
              variant="body2"
              tag="span"
              className={clsx(styles.llm_text_medium_gray, "me-5")}
            >
              Thinking
            </Typography>
            <Icon name={isOpen ? "chevron-up" : "chevron-down"} />
          </div>
        }
        transitionTime={250}
        easing="ease-in-out"
      >
        <div className={"mt-10 px-20"}>
          <Typography variant="body2" className={styles.llm_text_gray}>
            {step.text}
          </Typography>
        </div>
      </Collapsible>
    </div>
  );
};

const LLMThinkingSteps = ({ thinkingSteps, isProcessing }: Props) => {
  const [showAll, setShowAll] = useState(false);

  return (
    <div className={"flex flex-column"}>
      {thinkingSteps?.length > 0 && (
        <Link
          baseDecoration="underline"
          onClick={() => setShowAll(!showAll)}
          style={{ cursor: "pointer" }}
          className={clsx(styles.llm_text_gray, "pb-10")}
        >
          {showAll ? "Hide AI Reasoning Details" : "Show AI Reasoning Details"}
        </Link>
      )}

      {thinkingSteps?.length > 0 && (
        <Collapsible
          open={showAll}
          transitionTime={250}
          easing="ease-in-out"
          trigger={<></>}
        >
          <div className="flex flex-column flex-gap-6 mb-20">
            {thinkingSteps.map((step, idx) => (
              <ThinkingStepItem key={idx} step={step} />
            ))}
          </div>
        </Collapsible>
      )}

      {isProcessing && (
        <div className="flex align-center flex-gap-2 mb-20">
          <Spinner
            variant="secondary"
            size="sm"
            className={styles.llm_summary_processing_loader}
          />
          <span className={styles.llm_text_medium_gray}>Processing...</span>
        </div>
      )}
    </div>
  );
};

export default LLMThinkingSteps;
